import React, { useEffect, useMemo, useState } from "react";
import VsOpsLayout from "./VsOpsLayout";
import { VsOpsRequireAuth } from "./VsOpsLoginPage";
import { vsOpsApi } from "./api";
import { OTHER_STORE_LIST } from "../visoria/stores";

function formatDate(value) {
  if (!value) {
    return "—";
  }
  return new Date(value).toLocaleString("ru-RU");
}

function buildStoreMap(catalog = []) {
  return new Map(catalog.map((store) => [store.id, store]));
}

function parseKeyLines(value) {
  return String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function VsOpsWarehousePage() {
  const [stores, setStores] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [filters, setFilters] = useState({
    storeId: "",
    regionId: "",
    visoriaProductId: "",
    status: "available",
  });
  const [data, setData] = useState({ items: [], summary: [], total: 0 });
  const [form, setForm] = useState({
    storeId: "",
    regionId: "",
    visoriaProductId: "",
    productName: "",
    currencyCode: "",
    faceValue: "",
    note: "",
    keysText: "",
  });
  const [loading, setLoading] = useState(true);
  const [metaLoading, setMetaLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [metaError, setMetaError] = useState("");
  const [catalogWarning, setCatalogWarning] = useState("");
  const [message, setMessage] = useState("");
  const [denominationQuery, setDenominationQuery] = useState("");
  const platformStores = useMemo(
    () => (stores.length ? stores : OTHER_STORE_LIST),
    [stores],
  );
  const storeMap = useMemo(() => buildStoreMap(catalog), [catalog]);
  const selectedStore = useMemo(() => {
    const fromCatalog = storeMap.get(form.storeId);
    if (fromCatalog) {
      return fromCatalog;
    }
    if (!form.storeId) {
      return null;
    }
    const platform = platformStores.find((store) => store.id === form.storeId);
    return {
      id: form.storeId,
      label: platform?.label || form.storeId,
      regions: [],
    };
  }, [form.storeId, storeMap, platformStores]);
  const selectedRegion =
    selectedStore?.regions?.find((region) => region.id === form.regionId) || null;
  const selectedDenomination =
    selectedRegion?.denominations?.find(
      (denomination) => denomination.id === form.visoriaProductId,
    ) || null;
  const filteredDenominations = useMemo(() => {
    const denominations = selectedRegion?.denominations || [];
    const query = denominationQuery.trim().toLowerCase();
    if (!query) {
      return denominations;
    }
    return denominations.filter((denomination) =>
      denomination.label.toLowerCase().includes(query),
    );
  }, [selectedRegion, denominationQuery]);
  const keyLines = useMemo(() => parseKeyLines(form.keysText), [form.keysText]);
  const uniqueKeyCount = useMemo(() => new Set(keyLines).size, [keyLines]);
  const duplicateKeyCount = keyLines.length - uniqueKeyCount;

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => String(value).trim()),
      );
      const result = await vsOpsApi.getWarehouse(params);
      setData(result);
    } catch (loadError) {
      setError(loadError.message || "Ошибка загрузки склада");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setMetaLoading(true);
    setMetaError("");
    vsOpsApi
      .getWarehouseMeta()
      .then((meta) => {
        setStores(meta.stores?.length ? meta.stores : OTHER_STORE_LIST);
        setCatalog(meta.catalog || []);
        setCatalogWarning(meta.catalogWarning || "");
      })
      .catch((loadError) => {
        setStores(OTHER_STORE_LIST);
        setCatalog([]);
        setMetaError(
          loadError.message || "Не удалось загрузить список платформ и каталог",
        );
      })
      .finally(() => setMetaLoading(false));
    load();
  }, []);

  useEffect(() => {
    setForm((current) => {
      if (!current.storeId) {
        return current;
      }
      const store = storeMap.get(current.storeId);
      if (!store) {
        return current;
      }
      const regionExists = store.regions?.some((region) => region.id === current.regionId);
      if (regionExists) {
        return current;
      }
      return {
        ...current,
        regionId: "",
        visoriaProductId: "",
        productName: "",
        currencyCode: "",
        faceValue: "",
      };
    });
  }, [form.storeId, storeMap]);

  useEffect(() => {
    setForm((current) => {
      if (!current.regionId || !selectedStore) {
        return current;
      }
      const region = selectedStore.regions?.find((item) => item.id === current.regionId);
      const denominationExists = region?.denominations?.some(
        (denomination) => denomination.id === current.visoriaProductId,
      );
      if (denominationExists || !current.visoriaProductId) {
        return current;
      }
      return {
        ...current,
        visoriaProductId: "",
        productName: "",
        currencyCode: "",
        faceValue: "",
      };
    });
  }, [form.regionId, form.storeId, selectedStore]);

  useEffect(() => {
    if (!selectedDenomination) {
      return;
    }
    setForm((current) => ({
      ...current,
      productName: selectedDenomination.label,
      currencyCode: selectedDenomination.currencyCode || current.currencyCode,
      faceValue:
        selectedDenomination.faceValue != null
          ? String(selectedDenomination.faceValue)
          : current.faceValue,
    }));
  }, [selectedDenomination]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage("");
    setError("");
    try {
      const result = await vsOpsApi.addWarehouseCards({
        storeId: form.storeId,
        regionId: form.regionId,
        visoriaProductId: form.visoriaProductId,
        productName: form.productName,
        currencyCode: form.currencyCode,
        faceValue: form.faceValue,
        note: form.note,
        keysText: form.keysText,
      });

      const errorCount = result.errors?.length || 0;
      setMessage(
        `Добавлено карт: ${result.addedCount}${
          errorCount ? `, пропущено: ${errorCount}` : ""
        }`,
      );
      setForm((current) => ({ ...current, keysText: "" }));
      await load();
    } catch (submitError) {
      setError(submitError.message || "Не удалось добавить карты");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <VsOpsRequireAuth>
      <VsOpsLayout title="Склад">
        <section className="vs-ops-settings-block">
          <h2 className="vs-ops-subtitle">Добавить карты вручную</h2>
          <p className="vs-ops-muted">
            Карты попадут на локальный склад и будут выдаваться покупателям вместо
            покупки в Visoria, если совпадают магазин, регион и product id.
          </p>
          {metaLoading ? (
            <p className="vs-ops-muted">Загружаем платформы и каталог…</p>
          ) : null}
          {catalogWarning ? (
            <p className="vs-ops-error">
              Каталог Visoria недоступен: {catalogWarning}. Регионы и номиналы
              можно указать вручную.
            </p>
          ) : null}
          {metaError ? <p className="vs-ops-error">{metaError}</p> : null}

          <form className="vs-ops-settings-form" onSubmit={handleSubmit}>
            <label className="vs-ops-field">
              Платформа
              <select
                value={form.storeId}
                onChange={(event) =>
                  {
                    setDenominationQuery("");
                    setForm((current) => ({
                      ...current,
                      storeId: event.target.value,
                      regionId: "",
                      visoriaProductId: "",
                      productName: "",
                      currencyCode: "",
                      faceValue: "",
                    }));
                  }
                }
                required
              >
                <option value="">Выберите платформу</option>
                {platformStores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="vs-ops-field">
              Регион
              {selectedStore?.regions?.length ? (
                <select
                  value={form.regionId}
                  onChange={(event) => {
                    setDenominationQuery("");
                    setForm((current) => ({
                      ...current,
                      regionId: event.target.value,
                      visoriaProductId: "",
                      productName: "",
                      currencyCode: "",
                      faceValue: "",
                    }));
                  }}
                  required
                  disabled={!form.storeId}
                >
                  <option value="">Выберите регион</option>
                  {selectedStore.regions.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.label} ({region.id})
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  value={form.regionId}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      regionId: event.target.value.trim().toLowerCase(),
                      visoriaProductId: "",
                      productName: "",
                      currencyCode: "",
                      faceValue: "",
                    }))
                  }
                  placeholder="brl, usd, eur"
                  required
                  disabled={!form.storeId}
                />
              )}
            </label>

            <label className="vs-ops-field">
              Поиск по названию товара
              <input
                value={denominationQuery}
                onChange={(event) => setDenominationQuery(event.target.value)}
                placeholder="Например: 100 BRL, 10 USD, membership"
                disabled={!form.regionId || !filteredDenominations.length}
              />
            </label>

            <label className="vs-ops-field">
              Номинал
              <select
                value={form.visoriaProductId}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    visoriaProductId: event.target.value,
                  }))
                }
                required={filteredDenominations.length > 0}
                disabled={!form.regionId || !filteredDenominations.length}
              >
                <option value="">
                  {filteredDenominations.length
                    ? "Выберите номинал"
                    : "Укажите Visoria product id ниже"}
                </option>
                {filteredDenominations.map((denomination) => (
                  <option key={denomination.id} value={denomination.id}>
                    {denomination.label}
                  </option>
                ))}
              </select>
            </label>
            {selectedRegion && denominationQuery && filteredDenominations.length === 0 ? (
              <p className="vs-ops-muted">
                По запросу ничего не найдено в выбранном регионе.
              </p>
            ) : null}

            <label className="vs-ops-field">
              Visoria product id
              <input
                value={form.visoriaProductId}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    visoriaProductId: event.target.value,
                  }))
                }
                placeholder="019cca9f-a965-762c-8b5f-2a1b0ca592d0"
                required
              />
            </label>

            <label className="vs-ops-field">
              Название товара
              <input
                value={form.productName}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    productName: event.target.value,
                  }))
                }
                placeholder="Nintendo eShop 100 BRL"
                required
              />
            </label>

            <label className="vs-ops-field">
              Валюта
              <input
                value={form.currencyCode}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    currencyCode: event.target.value,
                  }))
                }
                placeholder="BRL"
              />
            </label>

            <label className="vs-ops-field">
              Face value
              <input
                value={form.faceValue}
                onChange={(event) =>
                  setForm((current) => ({ ...current, faceValue: event.target.value }))
                }
                placeholder="100"
              />
            </label>

            <label className="vs-ops-field">
              Комментарий
              <input
                value={form.note}
                onChange={(event) =>
                  setForm((current) => ({ ...current, note: event.target.value }))
                }
                placeholder="Откуда карта, зачем добавлена"
              />
            </label>

            <label className="vs-ops-field">
              Коды (по одному на строку)
              <textarea
                rows={6}
                value={form.keysText}
                onChange={(event) =>
                  setForm((current) => ({ ...current, keysText: event.target.value }))
                }
                placeholder={"XXXX-YYYY-ZZZZ\nAAAA-BBBB-CCCC"}
                required
              />
            </label>
            <div className="vs-ops-muted">
              Будет добавлено: {uniqueKeyCount}
              {duplicateKeyCount > 0 ? `, дублей в вставке: ${duplicateKeyCount}` : ""}
            </div>

            <button type="submit" className="vs-ops-btn" disabled={submitting}>
              {submitting
                ? "Добавляем…"
                : `Добавить на склад${uniqueKeyCount ? ` (${uniqueKeyCount})` : ""}`}
            </button>
          </form>

          {message ? <p className="vs-ops-success">{message}</p> : null}
          {error ? <p className="vs-ops-error">{error}</p> : null}
        </section>

        <section className="vs-ops-withdraw-section">
          <h2 className="vs-ops-subtitle">Остатки на складе</h2>

          <form
            className="vs-ops-filters"
            onSubmit={(event) => {
              event.preventDefault();
              load();
            }}
          >
            <label>
              Магазин
              <input
                value={filters.storeId}
                onChange={(event) =>
                  setFilters((current) => ({ ...current, storeId: event.target.value }))
                }
              />
            </label>
            <label>
              Регион
              <input
                value={filters.regionId}
                onChange={(event) =>
                  setFilters((current) => ({ ...current, regionId: event.target.value }))
                }
              />
            </label>
            <label>
              Product id
              <input
                value={filters.visoriaProductId}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    visoriaProductId: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Статус
              <select
                value={filters.status}
                onChange={(event) =>
                  setFilters((current) => ({ ...current, status: event.target.value }))
                }
              >
                <option value="available">available</option>
                <option value="reserved">reserved</option>
                <option value="resold">resold</option>
              </select>
            </label>
            <button type="submit" className="vs-ops-btn" disabled={loading}>
              Обновить
            </button>
          </form>

          <h3 className="vs-ops-subtitle">Сводка (available)</h3>
          <div className="vs-ops-table-wrap">
            <table className="vs-ops-table">
              <thead>
                <tr>
                  <th>Магазин</th>
                  <th>Регион</th>
                  <th>Товар</th>
                  <th>Product id</th>
                  <th>Доступно</th>
                </tr>
              </thead>
              <tbody>
                {data.summary?.map((item) => (
                  <tr
                    key={`${item.storeId}:${item.regionId}:${item.visoriaProductId}`}
                  >
                    <td>{item.storeId}</td>
                    <td>{item.regionId}</td>
                    <td>{item.productName}</td>
                    <td>
                      <code>{item.visoriaProductId}</code>
                    </td>
                    <td>{item.availableCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="vs-ops-subtitle">Карты ({data.total})</h3>
          <div className="vs-ops-table-wrap">
            <table className="vs-ops-table">
              <thead>
                <tr>
                  <th>Добавлена</th>
                  <th>Код</th>
                  <th>Магазин</th>
                  <th>Регион</th>
                  <th>Товар</th>
                  <th>Статус</th>
                  <th>Источник</th>
                  <th>Комментарий</th>
                </tr>
              </thead>
              <tbody>
                {data.items?.map((item) => (
                  <tr key={item.id}>
                    <td>{formatDate(item.returnedAt)}</td>
                    <td className="vs-ops-codes-cell">
                      <code>{item.keyCode}</code>
                    </td>
                    <td>{item.storeId}</td>
                    <td>{item.regionId}</td>
                    <td>{item.productName}</td>
                    <td>{item.status}</td>
                    <td>
                      {item.sourceOrderId === "manual_import"
                        ? "ручное"
                        : item.sourceOrderId}
                    </td>
                    <td>{item.returnReason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </VsOpsLayout>
    </VsOpsRequireAuth>
  );
}

export default VsOpsWarehousePage;
